import axios from 'axios';
import React, { useContext, useEffect, useState } from 'react';
import { CoinContext } from '../../context/CoinContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {faClose, faFileContract, faHome, faWallet, faServer, faChartBar, faBars, faTimes, faKey} from '@fortawesome/free-solid-svg-icons';
import { FormatDate } from '../../Helpers/Utils';
import { useNavigate } from 'react-router-dom';
import Wallet from './Wallet';
import Hosting from './Hosting';
import HostingContractAgreement from './HostingContractAgreement';
import ApiKeys from './ApiKeys';

interface WalletType {
    id: string;
    name: string;
}

interface WalletRow {
    userName: string;
    wallet: string;
    active: boolean;
    changeTime: string;
    walletType: string;
}

interface settingProps {
    activeTabExit: () => void;
}

type ActiveSection = 'wallet' | 'hosting-contract' | 'hosting-reports' | 'api';

function Settings({ activeTabExit }: settingProps) {
    const [activeSection, setActiveSection] = useState<ActiveSection>('hosting-contract');
    const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);

    const sidebarItems = [
        {
            id: 'hosting-contract' as ActiveSection,
            label: 'Hosting Agreement',
            icon: faFileContract,
            component: <HostingContractAgreement />
        },
        {
            id: 'hosting-reports' as ActiveSection,
            label: 'Hosting Reports',
            icon: faChartBar,
            component: <Hosting />
        },
        {
            id: 'wallet' as ActiveSection,
            label: 'Wallet',
            icon: faWallet,
            component: <Wallet />
        },
        {
            id: 'api' as ActiveSection,
            label: 'API',
            icon: faKey,
            component: <ApiKeys />
        }
    ];

    const renderActiveComponent = () => {
        const activeItem = sidebarItems.find(item => item.id === activeSection);
        return activeItem?.component || <HostingContractAgreement />;
    };

    const handleSidebarItemClick = (sectionId: ActiveSection) => {
        setActiveSection(sectionId);
        setSidebarOpen(false); // Close mobile sidebar after selection
    };

    const getActiveItemLabel = () => {
        const activeItem = sidebarItems.find(item => item.id === activeSection);
        return activeItem?.label || 'Settings';
    };

    return (
        <div className="flex min-h-screen bg-gray-50">
            {/* Mobile sidebar overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                ></div>
            )}

            {/* Sidebar */}
            <div className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out ${
                sidebarOpen ? 'translate-x-0' : '-translate-x-full'
            } lg:translate-x-0`}>
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h2 className="text-xl font-bold text-gray-800">Settings</h2>
                    <button
                        onClick={() => setSidebarOpen(false)}
                        className="lg:hidden p-2 rounded-md text-gray-500 hover:text-gray-700"
                    >
                        <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
                    </button>
                </div>
                <nav className="mt-6 flex-1">
                    {sidebarItems.map((item) => (
                        <button
                            key={item.id}
                            onClick={() => handleSidebarItemClick(item.id)}
                            className={`w-full text-left px-6 py-3 flex items-center gap-3 transition-colors duration-200 ${
                                activeSection === item.id
                                    ? 'bg-blue-50 text-blue-600 border-r-2 border-blue-600'
                                    : 'text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            <FontAwesomeIcon icon={item.icon} className="w-5 h-5 flex-shrink-0" />
                            <span className="font-medium">{item.label}</span>
                        </button>
                    ))}
                </nav>
                <div className="p-6 border-t border-gray-200">
                    <button
                        type="button"
                        onClick={activeTabExit}
                        className="w-full gap-2 flex content-center items-center justify-center py-3 px-4 text-sm font-medium text-gray-900 focus:outline-none bg-white rounded-lg border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:ring-gray-100 transition-colors duration-200"
                    >
                        <FontAwesomeIcon icon={faHome} />
                        <span>Back to Home</span>
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col lg:ml-0">
                {/* Mobile header */}
                <div className="lg:hidden bg-white shadow-sm border-b border-gray-200 px-4 py-3">
                    <div className="flex items-center justify-between">
                        <button
                            onClick={() => setSidebarOpen(true)}
                            className="p-2 rounded-md text-gray-500 hover:text-gray-700"
                        >
                            <FontAwesomeIcon icon={faBars} className="w-6 h-6" />
                        </button>
                        <h1 className="text-lg font-semibold text-gray-900">{getActiveItemLabel()}</h1>
                        <button
                            onClick={activeTabExit}
                            className="p-2 rounded-md text-gray-500 hover:text-gray-700"
                        >
                            <FontAwesomeIcon icon={faHome} className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Content area */}
                <div className="flex-1 overflow-auto">
                    <div className="p-4 lg:p-6">
                        {renderActiveComponent()}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Settings;
